import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Upload } from "lucide-react";

const BLANK = {
  name: "", sku: "", brand: "Sambiva", category: "Farofa & Pantry", description: "",
  ingredients: "", allergens: "", origin: "Brazil", storage: "Ambient", pack_size: "",
  case_quantity: 12, unit_weight: "", trade_price: 0, stock_quantity: 0, low_stock_threshold: 10,
  images: [], featured: false, active: true, dietary: [], nutrition: "", preparation: "",
  translations: { pt: { name: "", description: "" }, es: { name: "", description: "" } },
};

export default function AdminProductForm() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { document.title = `${isNew ? "New" : "Edit"} Product | Sambiva Admin`; }, [isNew]);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: existing } = useQuery({ queryKey: ["admin-product", id], enabled: !isNew, queryFn: async () => (await api.get(`/admin/products/${id}`)).data });

  useEffect(() => {
    if (existing) setForm({ ...BLANK, ...existing, translations: { pt: existing.translations?.pt || { name: "", description: "" }, es: existing.translations?.es || { name: "", description: "" } } });
  }, [existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTr = (lang, k, v) => setForm((f) => ({ ...f, translations: { ...f.translations, [lang]: { ...f.translations[lang], [k]: v } } }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [data.url]);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, trade_price: Number(form.trade_price), stock_quantity: Number(form.stock_quantity), case_quantity: Number(form.case_quantity), low_stock_threshold: Number(form.low_stock_threshold), images: form.images.filter(Boolean), dietary: Array.isArray(form.dietary) ? form.dietary : String(form.dietary).split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (isNew) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${id}`, payload);
      qc.invalidateQueries();
      toast.success("Product saved.");
      navigate("/admin/catalogue");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  const field = "w-full px-3.5 py-2.5 rounded-lg border border-black/10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sambiva-green/40";
  const label = "block text-xs font-semibold text-sambiva-muted uppercase tracking-wide mb-1.5";

  return (
    <AdminLayout title={isNew ? "New Product" : "Edit Product"}>
      <button onClick={() => navigate("/admin/catalogue")} className="flex items-center gap-1 text-sambiva-muted hover:text-sambiva-green mb-5 text-sm"><ChevronLeft size={16} /> Catalogue</button>
      <form onSubmit={save} data-testid="product-form" className="max-w-3xl space-y-6">
        <Section title="Basics">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Name *</label><input data-testid="pf-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={field} /></div>
            <div><label className={label}>SKU *</label><input data-testid="pf-sku" required value={form.sku} onChange={(e) => set("sku", e.target.value)} className={field} /></div>
            <div><label className={label}>Brand</label><input value={form.brand} onChange={(e) => set("brand", e.target.value)} className={field} /></div>
            <div><label className={label}>Category</label><select data-testid="pf-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={field}>{categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
          </div>
          <div><label className={label}>Description</label><textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={field} /></div>
          <div>
            <label className={label}>Product Image</label>
            <div className="flex items-center gap-3">
              <label data-testid="pf-upload-label" className="inline-flex items-center gap-2 cursor-pointer bg-sambiva-green/10 text-sambiva-green font-semibold px-4 py-2.5 rounded-lg text-sm hover:bg-sambiva-green/20 transition-colors">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} {uploading ? "Uploading…" : "Upload image"}
                <input data-testid="pf-upload" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
              {form.images[0] && <img src={form.images[0]} alt="" className="w-16 h-16 rounded-lg object-cover border border-black/10" />}
            </div>
            <input data-testid="pf-image" value={form.images[0] || ""} onChange={(e) => set("images", [e.target.value])} className={`${field} mt-2`} placeholder="…or paste an image URL" />
          </div>
        </Section>

        <Section title="Details">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Ingredients</label><input value={form.ingredients} onChange={(e) => set("ingredients", e.target.value)} className={field} /></div>
            <div><label className={label}>Allergens</label><input value={form.allergens} onChange={(e) => set("allergens", e.target.value)} className={field} /></div>
            <div><label className={label}>Origin</label><input value={form.origin} onChange={(e) => set("origin", e.target.value)} className={field} /></div>
            <div><label className={label}>Storage</label><select value={form.storage} onChange={(e) => set("storage", e.target.value)} className={field}><option>Ambient</option><option>Frozen</option><option>Chilled</option></select></div>
            <div><label className={label}>Pack Size</label><input value={form.pack_size} onChange={(e) => set("pack_size", e.target.value)} className={field} /></div>
            <div><label className={label}>Unit Weight</label><input value={form.unit_weight} onChange={(e) => set("unit_weight", e.target.value)} className={field} /></div>
            <div><label className={label}>Dietary (comma separated)</label><input value={Array.isArray(form.dietary) ? form.dietary.join(", ") : form.dietary} onChange={(e) => set("dietary", e.target.value)} className={field} /></div>
            <div><label className={label}>Preparation</label><input value={form.preparation} onChange={(e) => set("preparation", e.target.value)} className={field} /></div>
          </div>
          <div><label className={label}>Nutrition</label><input value={form.nutrition} onChange={(e) => set("nutrition", e.target.value)} className={field} /></div>
        </Section>

        <Section title="Trade & Inventory">
          <div className="grid sm:grid-cols-4 gap-4">
            <div><label className={label}>Trade Price €</label><input data-testid="pf-price" type="number" step="0.01" value={form.trade_price} onChange={(e) => set("trade_price", e.target.value)} className={field} /></div>
            <div><label className={label}>Case Qty</label><input type="number" value={form.case_quantity} onChange={(e) => set("case_quantity", e.target.value)} className={field} /></div>
            <div><label className={label}>Stock Qty</label><input data-testid="pf-stock" type="number" value={form.stock_quantity} onChange={(e) => set("stock_quantity", e.target.value)} className={field} /></div>
            <div><label className={label}>Low Threshold</label><input type="number" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className={field} /></div>
          </div>
          <div className="flex gap-6 mt-2">
            <label className="flex items-center gap-2 text-sm"><input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#075A35]" /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-[#075A35]" /> Active</label>
          </div>
        </Section>

        <Section title="Translations">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Name (PT)</label><input value={form.translations.pt.name} onChange={(e) => setTr("pt", "name", e.target.value)} className={field} /></div>
            <div><label className={label}>Name (ES)</label><input value={form.translations.es.name} onChange={(e) => setTr("es", "name", e.target.value)} className={field} /></div>
            <div><label className={label}>Description (PT)</label><input value={form.translations.pt.description} onChange={(e) => setTr("pt", "description", e.target.value)} className={field} /></div>
            <div><label className={label}>Description (ES)</label><input value={form.translations.es.description} onChange={(e) => setTr("es", "description", e.target.value)} className={field} /></div>
          </div>
        </Section>

        <div className="flex gap-3">
          <button data-testid="pf-save" disabled={loading} type="submit" className="bg-sambiva-green hover:bg-sambiva-deepgreen text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2 disabled:opacity-60 transition-colors">{loading && <Loader2 className="animate-spin" size={18} />} Save Product</button>
          <button type="button" onClick={() => navigate("/admin/catalogue")} className="px-6 py-3 rounded-full border border-black/10 font-semibold text-sambiva-charcoal">Cancel</button>
        </div>
      </form>
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6">
      <h3 className="font-semibold text-sambiva-charcoal mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
